import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  ShieldCheck, 
  Heart, 
  ShoppingBag, 
  Truck, 
  Check, 
  Share2, 
  Sparkles, 
  MessageCircle,
  Copy,
  Minus,
  Plus,
  Layers,
  ChevronRight,
  Maximize2,
  Scale,
  Award,
  ArrowLeft,
  Star,
  Calculator,
  Gem,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Play,
  Video,
  X,
  Tag,
  MapPin,
  Zap
} from 'lucide-react';
import { formatINR, getPriceBreakup } from '../data/products';
import ProductCard from '../components/ProductCard';
import { resolveProductImage, handleProductImageError } from '../utils/imageHelper';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    products, 
    addToCart, 
    wishlist, 
    toggleWishlist, 
    masterMetalRates, 
    calculateProductPrice, 
    getMasterRateForProduct,
    storeSettings,
    addToast
  } = useShop();

  const product = (Array.isArray(products) ? products : []).find(
    (p) => String(p.id) === String(id) || String(p.sku) === String(id)
  );

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [copiedSku, setCopiedSku] = useState(false);
  const [selectedSize, setSelectedSize] = useState('7');
  const [isBreakupOpen, setIsBreakupOpen] = useState(true);
  const [isHoverZooming, setIsHoverZooming] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [imageFitMode, setImageFitMode] = useState('contain');
  const [pincode, setPincode] = useState('302001');
  const [pincodeStatus, setPincodeStatus] = useState({
    checked: true,
    valid: true,
    text: 'Delivery in 2-4 Days | FREE Delivery'
  });
  const mainStageRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveImageIndex(0);
    setIsVideoActive(false);
    setIsLightboxOpen(false);
    setLightboxZoom(1);
    setQty(1);
    if (product?.size) setSelectedSize(product.size);
  }, [id, product]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        setIsLightboxOpen(false);
        setLightboxZoom(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  const handleMouseMove = (e) => {
    if (isVideoActive || !mainStageRef.current) return;
    const rect = mainStageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  if (!product) {
    return (
      <div className="container" style={{ padding: '5rem 1rem', textAlign: 'center', minHeight: '60vh' }}>
        <h2 style={{ color: '#fff', fontSize: '1.8rem', marginBottom: '1rem' }}>Product Not Found</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
          The jewellery piece you are looking for is either sold out or no longer listed.
        </p>
        <Link to="/" className="btn btn-gold" style={{ padding: '0.75rem 1.5rem', fontWeight: 700 }}>
          <ArrowLeft size={16} style={{ marginRight: '6px' }} /> Return to Collection
        </Link>
      </div>
    );
  }

  const pricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
  const isFavorited = Array.isArray(wishlist) && wishlist.includes(product.id);
  const livePrice = pricing?.finalPrice || Number(product.price) || 0;
  const rawWeight = pricing?.weight || parseFloat(product.netGoldWeight || product.goldWeight || product.weight || product.grossWeight || 1.0) || 1.0;
  
  const ratePerGram = (pricing?.ratePerGram && pricing.ratePerGram > 0) 
    ? pricing.ratePerGram 
    : (getMasterRateForProduct ? getMasterRateForProduct(product, masterMetalRates) : 6765);
    
  const goldAmount = (pricing?.metalValue !== undefined && pricing.metalValue > 0) 
    ? pricing.metalValue 
    : Math.round(rawWeight * ratePerGram);

  const diamondAmount = pricing?.diamondAmount || 0;
  const gemstoneAmount = pricing?.gemstoneAmount || 0;
    
  const makingChargePct = pricing?.makingChargePercent !== undefined ? pricing.makingChargePercent : 6.99;
  const makingChargesDiscounted = (pricing?.makingCharge !== undefined && pricing.makingCharge > 0) 
    ? pricing.makingCharge 
    : Math.round(goldAmount * (makingChargePct / 100));
    
  const taxableAmount = pricing?.taxableAmount || (goldAmount + diamondAmount + gemstoneAmount + makingChargesDiscounted);
  const gstAmount = pricing?.gstAmount || Math.round(taxableAmount * 0.03);

  const breakup = getPriceBreakup({
    ...product,
    ...pricing,
    price: livePrice,
    metalValue: goldAmount,
    goldAmount: goldAmount,
    diamondAmount,
    gemstoneAmount,
    makingCharge: makingChargesDiscounted,
    makingChargesDiscounted: makingChargesDiscounted,
    makingChargePercent: makingChargePct,
    taxableAmount,
    gstAmount
  });

  const displayGoldAmount = 
    (breakup?.goldAmount && Number(breakup.goldAmount) > 0) ? Number(breakup.goldAmount) :
    (goldAmount && Number(goldAmount) > 0) ? Number(goldAmount) :
    (pricing?.metalValue && Number(pricing.metalValue) > 0) ? Number(pricing.metalValue) :
    (rawWeight > 0 && ratePerGram > 0) ? Math.round(rawWeight * ratePerGram) :
    Math.round((livePrice / 1.03) * 0.90);

  const displayTaxableAmount = Number(breakup?.taxableAmount) || Number(taxableAmount) || Math.round(livePrice / 1.03);

  const displayMakingCharges = 
    (breakup?.makingChargesDiscounted && Number(breakup.makingChargesDiscounted) > 0) ? Number(breakup.makingChargesDiscounted) :
    (makingChargesDiscounted && Number(makingChargesDiscounted) > 0) ? Number(makingChargesDiscounted) :
    (pricing?.makingCharge && Number(pricing.makingCharge) > 0) ? Number(pricing.makingCharge) :
    Math.max(0, displayTaxableAmount - displayGoldAmount - Number(breakup?.diamondAmount || 0) - Number(breakup?.gemstoneAmount || 0));

  const safeId = String(product.id || '');
  const sku = product.sku || `ML-${(product.category || 'JW').toUpperCase().slice(0, 2)}-${safeId.replace(/[^0-9]/g, '') || '10554'}`;
  const origin = product.origin || 'India';
  const sizes = product.sizes || (
    product.category === 'rings' ? ['6', '7', '8', '9', '10', '12'] :
    product.category === 'bangles' ? ['2.4', '2.6', '2.8'] :
    product.category === 'necklaces' ? ['16 Inch', '18 Inch', 'Choker'] :
    ['Standard']
  );

  const rawImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [product.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80'];
  const productImages = rawImages.map(img => resolveProductImage(img, product));

  const relatedProducts = (Array.isArray(products) ? products : [])
    .filter(p => p && p.id !== product.id && (p.category === product.category || !product.category))
    .slice(0, 4);

  const handleCopySku = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(sku);
      setCopiedSku(true);
      addToast(`SKU ${sku} copied to clipboard!`, 'success');
      setTimeout(() => setCopiedSku(false), 2500);
    }
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      addToast('Product link copied to clipboard!', 'info');
    }
  };

  const handleWhatsAppInquiry = () => {
    const phone = (storeSettings?.phone || storeSettings?.whatsapp || '919876543210').replace(/[^0-9]/g, '');
    const netWt = product.netGoldWeight || product.goldWeight || `${rawWeight}g`;
    const purity = product.purity || product.goldPurity || '22K Hallmarked';
    const msg = `*💎 INQUIRY & QUOTE - ML JEWELLERS*\n` +
      `• Product: ${product.name}\n` +
      `• SKU: ${sku}\n` +
      `• Net Weight: ${netWt}\n` +
      `• Purity: ${purity}\n` +
      `• Selected Size: ${selectedSize}\n` +
      `• Locked Rate Price: ${formatINR(livePrice)}\n\n` +
      `Please confirm live booking and dispatch timeline.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    addToast('Connecting to WhatsApp with full product specs!', 'success');
  };

  const handleCheckPincode = (e) => {
    e.preventDefault();
    if (pincode.trim().length === 6) {
      setPincodeStatus({
        checked: true,
        valid: true,
        text: 'Delivery in 2-4 Days | FREE Delivery'
      });
      addToast(`Pincode ${pincode} verified! Insured armored transit available.`, 'success');
    } else {
      setPincodeStatus({
        checked: true,
        valid: false,
        text: 'Please enter a valid 6-digit Pincode'
      });
    }
  };

  return (
    <div className="fk-pdp-page-wrap" style={{ backgroundColor: '#f1f3f6', color: '#212121', paddingBottom: '3rem' }}>
      
      {/* Breadcrumb Header */}
      <div style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        padding: '10px 0',
        marginBottom: '12px'
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', color: '#878787' }}>
          <Link to="/" style={{ color: '#2874f0', textDecoration: 'none' }}>Home</Link>
          <ChevronRight size={14} />
          <Link to={`/category/${product.category || 'all'}`} style={{ color: '#2874f0', textDecoration: 'none', textTransform: 'capitalize' }}>
            {product.category || 'Jewellery'}
          </Link>
          <ChevronRight size={14} />
          <span style={{ color: '#212121', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
            {product.name}
          </span>
        </div>
      </div>

      <div className="container">
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '2px',
          border: '1px solid #e0e0e0',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.08)',
          padding: '24px',
          display: 'grid',
          gridTemplateColumns: '1fr 1.2fr',
          gap: '2.5rem',
          alignItems: 'start'
        }} className="quickview-grid">

          {/* Left Column: Gallery */}
          <div style={{ position: 'sticky', top: '90px' }}>
            
            {/* Main Stage */}
            <div 
              ref={mainStageRef}
              onMouseEnter={() => !isVideoActive && setIsHoverZooming(true)}
              onMouseLeave={() => setIsHoverZooming(false)}
              onMouseMove={handleMouseMove}
              onClick={() => !isVideoActive && setIsLightboxOpen(true)}
              style={{
                borderRadius: '2px',
                overflow: 'hidden',
                height: '440px',
                background: '#ffffff',
                border: '1px solid #f0f0f0',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'none',
                cursor: isVideoActive ? 'default' : (isHoverZooming ? 'crosshair' : 'zoom-in')
              }}
            >
              {isVideoActive && product.videoUrl ? (
                <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {(product.videoUrl.includes('youtube.com') || product.videoUrl.includes('youtu.be')) ? (
                    <iframe
                      src={(() => {
                        const url = product.videoUrl;
                        let id = '';
                        if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1]?.split('?')[0];
                        else if (url.includes('youtube.com/shorts/')) id = url.split('youtube.com/shorts/')[1]?.split('?')[0];
                        else if (url.includes('youtube.com/watch')) {
                          const m = url.match(/[?&]v=([^&]+)/);
                          id = m ? m[1] : '';
                        } else if (url.includes('youtube.com/embed/')) return url;
                        return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=0&controls=1&rel=0` : url;
                      })()}
                      title="Product Video Reel"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        minHeight: '340px'
                      }}
                    />
                  ) : (
                    <video
                      src={product.videoUrl}
                      controls
                      autoPlay
                      loop
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  )}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid var(--border-gold)',
                    color: 'var(--gold-400)',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.6rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    pointerEvents: 'none'
                  }}>
                    <Video size={13} /> Product Video Reel
                  </div>
                </div>
              ) : (
                <>
                  <img
                    src={productImages[activeImageIndex] || productImages[0]}
                    alt={product.name}
                    onError={(e) => handleProductImageError(e, product)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: imageFitMode === 'contain' ? 'auto' : '100%',
                      height: imageFitMode === 'contain' ? 'auto' : '100%',
                      objectFit: imageFitMode,
                      padding: imageFitMode === 'contain' ? '12px' : '0',
                      transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                      transform: isHoverZooming ? 'scale(2.2)' : 'scale(1)',
                      transition: isHoverZooming ? 'transform 0.08s ease-out' : 'transform 0.3s ease-in-out',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />

                  {/* Zoom Hint Overlay */}
                  {isHoverZooming && (
                    <div style={{
                      position: 'absolute',
                      top: '14px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(10, 10, 15, 0.85)',
                      border: '1px solid var(--border-gold)',
                      color: 'var(--gold-400)',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.65rem',
                      borderRadius: '20px',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      zIndex: 4
                    }}>
                      <ZoomIn size={12} /> 2.2x Lens Zoom Active
                    </div>
                  )}

                  {/* Watch Video Reel Button */}
                  {product.videoUrl && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsVideoActive(true);
                        setIsHoverZooming(false);
                      }}
                      style={{
                        position: 'absolute',
                        bottom: '14px',
                        left: '14px',
                        background: 'linear-gradient(135deg, #d4af37, #f3e5ab)',
                        color: '#0a0a0f',
                        border: 'none',
                        padding: '0.35rem 0.85rem',
                        borderRadius: '999px',
                        fontSize: '0.74rem',
                        fontWeight: 800,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 15px rgba(212,175,55,0.4)',
                        zIndex: 5
                      }}
                    >
                      <Play size={13} fill="#0a0a0f" /> Watch Video Reel
                    </button>
                  )}

                  {/* Bottom Right Controls: Fit Mode + HD Lightbox */}
                  <div style={{
                    position: 'absolute',
                    bottom: '14px',
                    right: '14px',
                    display: 'flex',
                    gap: '6px',
                    zIndex: 5
                  }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageFitMode(prev => prev === 'contain' ? 'cover' : 'contain');
                      }}
                      style={{
                        background: 'rgba(15, 15, 25, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(212, 175, 55, 0.3)',
                        color: 'var(--gold-300)',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        padding: '0.25rem 0.55rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {imageFitMode === 'contain' ? '📐 Pura Photo' : '🔲 Fill'}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsLightboxOpen(true);
                      }}
                      style={{
                        background: 'rgba(212, 175, 55, 0.25)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(212, 175, 55, 0.45)',
                        color: '#fff',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
                      }}
                    >
                      <Maximize2 size={12} color="var(--gold-400)" /> HD Lightbox
                    </button>
                  </div>
                </>
              )}

              {/* Badges on Image */}
              <div style={{
                position: 'absolute',
                top: '14px',
                left: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                zIndex: 3,
                pointerEvents: 'none'
              }}>
                <span style={{
                  background: 'rgba(10, 10, 15, 0.85)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid var(--border-gold)',
                  color: 'var(--gold-400)',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <ShieldCheck size={13} /> {product.category === 'kitchenware' ? 'Food Grade Certified' : (product.purity || 'BIS 916 Hallmarked')}
                </span>
                {product.category !== 'kitchenware' && (
                  <span style={{
                    background: 'rgba(16, 185, 129, 0.2)',
                    border: '1px solid #10b981',
                    color: '#34d399',
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    padding: '0.2rem 0.6rem',
                    borderRadius: '6px'
                  }}>
                    {makingChargePct}% Making Charges
                  </span>
                )}
              </div>

              {/* Wishlist Button on Image */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleWishlist(product.id);
                }}
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  width: '38px',
                  height: '38px',
                  borderRadius: '50%',
                  background: 'rgba(10, 10, 15, 0.85)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 6
                }}
              >
                <Heart size={18} color={isFavorited ? '#f43f5e' : '#fff'} fill={isFavorited ? '#f43f5e' : 'none'} />
              </button>
            </div>

            {/* Thumbnails + Video Reel Thumbnail */}
            {(productImages.length > 1 || product.videoUrl) && (
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', overflowX: 'auto', paddingBottom: '4px' }}>
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setActiveImageIndex(idx);
                      setIsVideoActive(false);
                    }}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      border: (!isVideoActive && activeImageIndex === idx) ? '2px solid #2874f0' : '1px solid #e0e0e0',
                      background: '#ffffff',
                      padding: '3px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      opacity: (!isVideoActive && activeImageIndex === idx) ? 1 : 0.8,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <img 
                      src={img} 
                      alt="" 
                      onError={(e) => handleProductImageError(e, product)}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                    />
                  </button>
                ))}

                {/* Video Reel Thumbnail Option */}
                {product.videoUrl && (
                  <button
                    type="button"
                    onClick={() => setIsVideoActive(true)}
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      border: isVideoActive ? '2px solid #2874f0' : '1px solid #e0e0e0',
                      background: '#fafafa',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.15s ease'
                    }}
                    title="Watch Product Video Reel"
                  >
                    <div style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      background: '#2874f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '2px'
                    }}>
                      <Play size={12} fill="#ffffff" color="#ffffff" style={{ marginLeft: '1px' }} />
                    </div>
                    <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#2874f0', textTransform: 'uppercase' }}>
                      Video
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Flipkart Iconic Action Buttons (Directly under Image in Left Column) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => {
                  addToCart({
                    ...product,
                    price: livePrice,
                    selectedSize
                  }, qty);
                  addToast(`Added ${qty} × ${product.name} to Cart`, 'success');
                  navigate('/cart');
                }}
                style={{
                  height: '50px',
                  backgroundColor: '#ff9f00',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,.2)',
                  textTransform: 'uppercase'
                }}
              >
                <ShoppingBag size={18} /> ADD TO CART
              </button>

              <button
                type="button"
                onClick={() => {
                  addToCart({
                    ...product,
                    price: livePrice,
                    selectedSize
                  }, qty);
                  navigate('/checkout');
                }}
                style={{
                  height: '50px',
                  backgroundColor: '#fb641b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,.2)',
                  textTransform: 'uppercase'
                }}
              >
                <Zap size={18} /> BUY NOW
              </button>
            </div>

            {/* Quantity Selector + Instant WhatsApp Quote */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#ffffff',
                border: '1px solid #dcdcdc',
                borderRadius: '2px',
                height: '38px'
              }}>
                <button
                  type="button"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  style={{ background: 'none', border: 'none', color: '#212121', padding: '0 0.7rem', cursor: 'pointer', fontSize: '1rem', fontWeight: 700 }}
                >
                  -
                </button>
                <span style={{ padding: '0 0.3rem', fontWeight: 700, fontSize: '0.88rem', color: '#212121', minWidth: '22px', textAlign: 'center' }}>{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty(qty + 1)}
                  style={{ background: 'none', border: 'none', color: '#212121', padding: '0 0.7rem', cursor: 'pointer', fontSize: '1rem', fontWeight: 700 }}
                >
                  +
                </button>
              </div>

              <button
                type="button"
                onClick={handleWhatsAppInquiry}
                style={{
                  flex: 1,
                  height: '38px',
                  backgroundColor: '#25D366',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
                title="Instant WhatsApp Specs Quote"
              >
                <MessageCircle size={15} /> Instant WhatsApp Quote
              </button>
            </div>
          </div>

          {/* Right Column: Information & Purchase Controls */}
          <div>
            
            {/* Title & Tagline */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#878787', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>
                  Brand: M.L. Jewellers Flagship
                </span>
                <button
                  onClick={handleShare}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2874f0',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Share2 size={14} /> Share
                </button>
              </div>

              <h1 style={{
                fontSize: '1.35rem',
                fontWeight: 600,
                color: '#212121',
                lineHeight: 1.3,
                marginBottom: '8px'
              }}>
                {product.name}
              </h1>

              {/* Flipkart Green Rating Pill & Assured Tag */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{
                  backgroundColor: '#388e3c',
                  color: '#ffffff',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '3px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px'
                }}>
                  {product.rating || '4.8'} <Star size={11} fill="#ffffff" color="#ffffff" />
                </span>

                <span style={{ color: '#878787', fontSize: '0.85rem' }}>
                  1,428 Ratings & 234 Reviews
                </span>

                <span style={{
                  backgroundColor: '#2874f0',
                  color: '#ffffff',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: '2px',
                  fontStyle: 'italic'
                }}>
                  ML-Assured ★
                </span>
              </div>
            </div>

            {/* Flipkart Price Showcase */}
            <div style={{
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: '14px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '0.78rem', color: '#388e3c', fontWeight: 700, marginBottom: '2px' }}>
                Special price
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1.85rem', fontWeight: 800, color: '#212121' }}>
                  {formatINR(livePrice)}
                </span>
                <span style={{ fontSize: '1rem', color: '#878787', textDecoration: 'line-through' }}>
                  {formatINR(Math.round(livePrice * 1.15))}
                </span>
                <span style={{ fontSize: '1rem', color: '#388e3c', fontWeight: 700 }}>
                  13% off
                </span>
              </div>
              <div style={{ fontSize: '0.74rem', color: '#878787', marginTop: '4px' }}>
                Inclusive of 3% GST & insured delivery • Live Bullion Linked
              </div>
            </div>

            {/* Dynamic Flipkart "Available Offers" Section (Customizable via Admin) */}
            {storeSettings?.productOffersEnabled !== false && (
              <div style={{
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: '2px',
                padding: '14px 16px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#212121', marginBottom: '8px' }}>
                  Available offers
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.82rem', color: '#212121' }}>
                  {[
                    { title: storeSettings?.productOffer1Title || 'Bank Offer', text: storeSettings?.productOffer1Text || '10% Instant Discount up to ₹1,500 on HDFC / ICICI Credit Cards' },
                    { title: storeSettings?.productOffer2Title || 'Special Price', text: storeSettings?.productOffer2Text || 'Get direct artisan pricing with zero middleman wastage' },
                    { title: storeSettings?.productOffer3Title || 'Special Offer', text: storeSettings?.productOffer3Text || "Flat 50% OFF on Making Charges on today's orders" },
                    ...(storeSettings?.productOffer4Title && storeSettings?.productOffer4Text ? [{ title: storeSettings.productOffer4Title, text: storeSettings.productOffer4Text }] : [])
                  ].filter(o => o.title && o.text).map((off, oIdx) => (
                    <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Tag size={14} color="#388e3c" style={{ flexShrink: 0 }} />
                      <span><strong>{off.title}:</strong> {off.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Flipkart Delivery & Pincode Checker Section */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              padding: '12px 14px',
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: '2px',
              marginBottom: '16px'
            }}>
              <div style={{ minWidth: '85px', color: '#878787', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px', paddingTop: '4px' }}>
                <MapPin size={16} color="#878787" /> Delivery
              </div>
              <div style={{ flex: 1 }}>
                <form onSubmit={handleCheckPincode} style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '300px' }}>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="Enter Delivery Pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/[^0-9]/g, ''))}
                    style={{
                      border: 'none',
                      borderBottom: '2px solid #2874f0',
                      padding: '4px 6px',
                      fontSize: '0.88rem',
                      outline: 'none',
                      width: '100%',
                      color: '#212121',
                      background: 'transparent'
                    }}
                  />
                  <button
                    type="submit"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2874f0',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    Check
                  </button>
                </form>
                {pincodeStatus && (
                  <div style={{ marginTop: '8px', fontSize: '0.82rem' }}>
                    {pincodeStatus.valid ? (
                      <div>
                        <div style={{ color: '#212121', fontWeight: 600 }}>
                          Delivery by 3-5 Days | <span style={{ color: '#388e3c' }}>FREE</span>
                        </div>
                        <div style={{ color: '#878787', fontSize: '0.76rem', marginTop: '2px' }}>
                          {storeSettings?.deliverySubtitle || '✓ 100% Insured Armored Transit • Pre-Paid Verified Dispatch'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#d32f2f' }}>{pincodeStatus.text}</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Flipkart Product Specifications Card */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '2px',
              padding: '16px',
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              fontSize: '0.85rem'
            }}>
              <div style={{ fontWeight: 700, color: '#212121', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px', fontSize: '0.95rem' }}>
                Product Details & Specifications
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Vendor SKU</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#212121', marginRight: '8px' }}>{sku}</span>
                <button
                  onClick={handleCopySku}
                  style={{
                    background: '#f1f3f6',
                    border: '1px solid #e0e0e0',
                    borderRadius: '2px',
                    padding: '2px 8px',
                    color: copiedSku ? '#388e3c' : '#2874f0',
                    cursor: 'pointer',
                    fontSize: '0.72rem',
                    fontWeight: 600
                  }}
                >
                  {copiedSku ? 'Copied' : 'Copy'}
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Net Metal Weight</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ color: '#212121', fontWeight: 700 }}>
                  {product.netGoldWeight || product.goldWeight || `${rawWeight} Grams`}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Gold Purity</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ color: '#212121', fontWeight: 600 }}>{product.purity || product.goldPurity || '22KT (916 BIS)'}</span>
              </div>

              {product.category !== 'kitchenware' && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ width: '150px', color: '#878787' }}>Making Charges %</span>
                  <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                  <span style={{ color: '#388e3c', fontWeight: 700 }}>
                    {makingChargePct}% (Direct Artisan Rate)
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Live Gold Rate</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ color: '#212121', fontWeight: 600 }}>{formatINR(ratePerGram)} / gram</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Availability</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ 
                  color: product?.inStock === false ? '#d32f2f' : '#388e3c', 
                  fontWeight: 700, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px' 
                }}>
                  <span style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    background: product?.inStock === false ? '#d32f2f' : '#388e3c' 
                  }} />
                  {product?.availability 
                    || (product?.inStock === false ? 'Out of Stock / Made to Order' : (storeSettings?.defaultAvailability || 'In Stock (Dispatch in 24-48 Hours)'))}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{ width: '150px', color: '#878787' }}>Delivery</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <span style={{ color: '#212121', fontWeight: 600 }}>
                  {product?.delivery || storeSettings?.defaultDelivery || '3-5 Days (Insured Nationwide Transit)'}
                </span>
              </div>

              {/* Size Selector */}
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.6rem' }}>
                <span style={{ width: '150px', color: '#878787' }}>Select Size</span>
                <span style={{ color: '#878787', marginRight: '10px' }}>:</span>
                <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                  {sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSelectedSize(s)}
                      style={{
                        padding: '4px 12px',
                        fontSize: '0.8rem',
                        borderRadius: '2px',
                        background: selectedSize === s ? '#e8f0fe' : '#ffffff',
                        color: selectedSize === s ? '#2874f0' : '#212121',
                        border: selectedSize === s ? '2px solid #2874f0' : '1px solid #dcdcdc',
                        cursor: 'pointer',
                        fontWeight: selectedSize === s ? 700 : 500,
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* CARD: TRANSPARENT PRODUCT PRICE CALCULATOR & BREAKUP (Flipkart Theme) */}
            <div style={{
              borderRadius: '2px',
              border: '1px solid #e0e0e0',
              background: '#ffffff',
              overflow: 'hidden',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}>
              {/* Header with Minus / Plus toggle */}
              <button
                type="button"
                onClick={() => setIsBreakupOpen(!isBreakupOpen)}
                style={{
                  width: '100%',
                  padding: '0.85rem 1.1rem',
                  background: '#fafafa',
                  border: 'none',
                  borderBottom: isBreakupOpen ? '1px solid #e0e0e0' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  color: '#212121',
                  fontWeight: 700,
                  fontSize: '0.88rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Calculator size={17} color="#2874f0" />
                  <span style={{ color: '#212121' }}>TRANSPARENT PRICE CALCULATOR & BREAKUP</span>
                  <span style={{
                    fontSize: '0.66rem',
                    padding: '0.15rem 0.45rem',
                    borderRadius: '2px',
                    background: '#e8f0fe',
                    color: '#2874f0',
                    fontWeight: 800
                  }}>
                    LIVE BULLION LINKED
                  </span>
                </div>
                <span style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '2px',
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#212121'
                }}>
                  {isBreakupOpen ? <Minus size={14} /> : <Plus size={14} />}
                </span>
              </button>

              {/* Collapsible Breakup Content */}
              {isBreakupOpen && (
                <div style={{ padding: '1rem 1.15rem', fontSize: '0.84rem', color: '#212121' }}>
                  {/* Gold / Metal Component Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontWeight: 600, color: '#212121' }}>
                          {product.category === 'kitchenware' ? 'Cookware Base' : (product.metalType || 'Gold')}
                        </span>
                        <span style={{
                          background: '#fff9e6',
                          color: '#b8860b',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          padding: '0.1rem 0.4rem',
                          borderRadius: '2px',
                          border: '1px solid #ffe082'
                        }}>
                          {product.purity || 'Hallmarked'}
                        </span>
                      </div>
                      {product.category !== 'kitchenware' && (
                        <span style={{ color: '#878787', fontSize: '0.74rem', marginTop: '2px' }}>
                          ({breakup.goldWeight} @ {formatINR(ratePerGram)}/g live rate)
                        </span>
                      )}
                    </div>
                    <span style={{ fontWeight: 700, color: '#212121' }}>
                      {formatINR(displayGoldAmount)}
                    </span>
                  </div>

                  {/* Certified Diamonds Row (if applicable) */}
                  {(breakup.diamondAmount > 0 || (product.diamondCarat && !String(product.diamondCarat).toLowerCase().includes('n/a'))) && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <Gem size={14} color="#2874f0" />
                          <span style={{ fontWeight: 600, color: '#212121' }}>Natural Certified Diamonds</span>
                        </div>
                        <span style={{ color: '#878787', fontSize: '0.74rem', marginTop: '2px' }}>
                          {breakup.diamondWeight || product.diamondCarat}
                          {breakup.diamondPieces ? ` • ${breakup.diamondPieces} Pcs` : ''}
                          {breakup.diamondClarity && breakup.diamondClarity !== 'N/A' ? ` • ${breakup.diamondClarity}` : ''}
                        </span>
                      </div>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {formatINR(breakup.diamondAmount)}
                      </span>
                    </div>
                  )}

                  {/* Gemstone / Uncut Polki / Pearl Row (if applicable) */}
                  {(breakup.gemstoneAmount > 0 || (product.gemstone && product.gemstone !== 'None')) && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: '#212121' }}>Gemstones & Precious Stones</span>
                        <span style={{ color: '#878787', fontSize: '0.74rem', marginTop: '2px' }}>
                          {product.gemstone}
                        </span>
                      </div>
                      <span style={{ fontWeight: 700, color: '#212121' }}>
                        {formatINR(breakup.gemstoneAmount)}
                      </span>
                    </div>
                  )}

                  {/* Making Charges Row */}
                  {product.category !== 'kitchenware' && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ fontWeight: 600, color: '#212121' }}>Making Charges</span>
                          <span style={{ 
                            fontSize: '0.68rem', 
                            color: '#388e3c', 
                            fontWeight: 800,
                            background: '#e8f5e9',
                            padding: '0.1rem 0.35rem',
                            borderRadius: '2px',
                            border: '1px solid #c8e6c9'
                          }}>
                            {`${makingChargePct}% (Direct Artisan Rate)`}
                          </span>
                        </div>
                        <span style={{ color: '#878787', fontSize: '0.74rem', marginTop: '2px' }}>
                          Direct Artisan Wholesale Rate
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {(breakup?.makingChargesOriginal > displayMakingCharges) && (
                          <span style={{
                            color: '#878787',
                            textDecoration: 'line-through',
                            fontSize: '0.78rem'
                          }}>
                            {formatINR(breakup.makingChargesOriginal)}
                          </span>
                        )}
                        <span style={{ fontWeight: 700, color: '#212121' }}>
                          {formatINR(displayMakingCharges)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Taxable Subtotal */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.65rem',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <span style={{ color: '#878787', fontSize: '0.8rem' }}>Subtotal (Taxable Value)</span>
                    <span style={{ fontWeight: 600, color: '#212121' }}>
                      {formatINR(displayTaxableAmount)}
                    </span>
                  </div>

                  {/* GST (3%) Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px dashed #e0e0e0'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600, color: '#212121' }}>Statutory GST (3%)</span>
                      <span style={{ fontSize: '0.7rem', color: '#878787' }}>
                        (Precious metal tax on {formatINR(breakup.taxableAmount)})
                      </span>
                    </div>
                    <span style={{ fontWeight: 700, color: '#388e3c' }}>
                      + {formatINR(breakup.gstAmount)}
                    </span>
                  </div>

                  {/* Grand Total */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    paddingTop: '0.4rem',
                    backgroundColor: '#fafafa',
                    padding: '8px 10px',
                    borderRadius: '2px',
                    border: '1px solid #f0f0f0'
                  }}>
                    <div>
                      <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#212121' }}>
                        FINAL TOTAL PAYABLE
                      </span>
                      <div style={{ fontSize: '0.72rem', color: '#878787', marginTop: '1px' }}>
                        Inclusive of all taxes & insured doorstep delivery
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {breakup.originalTotal > breakup.finalTotal && (
                        <span style={{
                          display: 'block',
                          fontSize: '0.82rem',
                          color: '#878787',
                          textDecoration: 'line-through'
                        }}>
                          {formatINR(breakup.originalTotal)}
                        </span>
                      )}
                      <span style={{
                        fontSize: '1.35rem',
                        fontWeight: 800,
                        color: '#212121'
                      }}>
                        {formatINR(livePrice)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Seller Information Box - Flipkart Style */}
            <div style={{
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: '2px',
              padding: '14px 16px',
              marginBottom: '16px',
              fontSize: '0.84rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ color: '#878787', fontSize: '0.8rem' }}>Seller Information</div>
                <span style={{
                  backgroundColor: '#388e3c',
                  color: '#ffffff',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: '3px'
                }}>
                  4.9 ★
                </span>
              </div>
              <div style={{ fontWeight: 700, color: '#2874f0', fontSize: '0.92rem', marginBottom: '4px' }}>
                M.L. Jewellers Heritage Workshop, Jaipur
              </div>
              <div style={{ display: 'flex', gap: '14px', color: '#878787', fontSize: '0.78rem', flexWrap: 'wrap', marginTop: '6px' }}>
                <span>✓ 7 Days Replacement</span>
                <span>✓ 100% Genuine BIS Assayed</span>
                <span>✓ GST Invoice Available</span>
              </div>
            </div>

            {/* Product Description Box */}
            {product.description && (
              <div style={{
                background: '#fafafa',
                borderRadius: '2px',
                padding: '16px',
                border: '1px solid #e0e0e0',
                fontSize: '0.86rem',
                color: '#424242',
                lineHeight: 1.6
              }}>
                <div style={{ fontWeight: 700, color: '#212121', marginBottom: '6px', fontSize: '0.92rem' }}>
                  Product Description
                </div>
                {product.description}
              </div>
            )}

          </div>
        </div>

        {/* Flipkart Style Similar Products Section */}
        {relatedProducts.length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            backgroundColor: '#ffffff',
            borderRadius: '2px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.08)',
            padding: '20px 24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: '12px',
              marginBottom: '16px'
            }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#212121', margin: 0 }}>
                  Similar Products
                </h2>
                <p style={{ fontSize: '0.8rem', color: '#878787', margin: '4px 0 0 0' }}>
                  Handpicked matching pieces in {product.category || 'collection'}
                </p>
              </div>
              <Link 
                to={`/category/${product.category || 'all'}`}
                style={{
                  backgroundColor: '#2874f0',
                  color: '#ffffff',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  padding: '6px 14px',
                  borderRadius: '2px',
                  textDecoration: 'none'
                }}
              >
                VIEW ALL
              </Link>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '1rem'
            }}>
              {relatedProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Fullscreen HD Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(5, 5, 8, 0.94)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => {
            setIsLightboxOpen(false);
            setLightboxZoom(1);
          }}
        >
          {/* Top Bar Controls */}
          <div 
            style={{
              position: 'absolute',
              top: '20px',
              left: '24px',
              right: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 10
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '1rem', letterSpacing: '0.05em' }}>
                {product.name}
              </span>
              <span style={{
                background: 'rgba(212, 175, 55, 0.15)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                color: 'var(--gold-300)',
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '0.2rem 0.6rem',
                borderRadius: '999px'
              }}>
                HD Inspection View
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <button
                type="button"
                onClick={() => setLightboxZoom(prev => Math.max(1, +(prev - 0.5).toFixed(1)))}
                disabled={lightboxZoom <= 1}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: lightboxZoom <= 1 ? 'rgba(255,255,255,0.25)' : '#fff',
                  width: '38px',
                  height: '38px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: lightboxZoom <= 1 ? 'not-allowed' : 'pointer'
                }}
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>

              <span style={{ color: 'var(--gold-400)', fontWeight: 700, fontSize: '0.85rem', minWidth: '46px', textAlign: 'center' }}>
                {Math.round(lightboxZoom * 100)}%
              </span>

              <button
                type="button"
                onClick={() => setLightboxZoom(prev => Math.min(3.5, +(prev + 0.5).toFixed(1)))}
                disabled={lightboxZoom >= 3.5}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: lightboxZoom >= 3.5 ? 'rgba(255,255,255,0.25)' : '#fff',
                  width: '38px',
                  height: '38px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: lightboxZoom >= 3.5 ? 'not-allowed' : 'pointer'
                }}
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>

              <button
                type="button"
                onClick={() => setLightboxZoom(1)}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  padding: '0 0.8rem',
                  height: '38px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
                title="Reset Zoom"
              >
                <RotateCcw size={15} /> Reset
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsLightboxOpen(false);
                  setLightboxZoom(1);
                }}
                style={{
                  background: 'rgba(244, 63, 94, 0.15)',
                  border: '1px solid rgba(244, 63, 94, 0.35)',
                  color: '#fb7185',
                  width: '38px',
                  height: '38px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginLeft: '0.5rem'
                }}
                title="Close (Esc)"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Centered Image with Zoom */}
          <div 
            style={{
              flex: 1,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: lightboxZoom > 1 ? 'grab' : 'zoom-in',
              padding: '4rem 1rem 6rem'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxZoom(prev => prev === 1 ? 2 : 1);
            }}
          >
            <img
              src={productImages[activeImageIndex] || productImages[0]}
              alt={product.name}
              style={{
                maxWidth: '85vw',
                maxHeight: '75vh',
                objectFit: 'contain',
                transform: `scale(${lightboxZoom})`,
                transition: 'transform 0.25s ease',
                filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.9))'
              }}
            />
          </div>

          {/* Bottom Thumbnails Strip */}
          <div 
            style={{
              position: 'absolute',
              bottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
              zIndex: 10
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {productImages.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', padding: '0.2rem' }}>
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setActiveImageIndex(idx);
                      setLightboxZoom(1);
                    }}
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: activeImageIndex === idx ? '2px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.2)',
                      background: '#0a0a10',
                      cursor: 'pointer',
                      padding: 0,
                      opacity: activeImageIndex === idx ? 1 : 0.6,
                      transform: activeImageIndex === idx ? 'scale(1.08)' : 'scale(1)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </button>
                ))}
              </div>
            )}
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.72rem', margin: 0 }}>
              💡 Double-click image to zoom in/out • Press <kbd style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 5px', borderRadius: '4px' }}>Esc</kbd> or click outside to close
            </p>
          </div>
        </div>
      )}

      {/* Flipkart Iconic Mobile Sticky Action Bar (Fixed at bottom on phones) */}
      <div 
        className="fk-mobile-sticky-cta"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '52px',
          backgroundColor: '#ffffff',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.15)',
          zIndex: 960,
          display: 'none',
          gridTemplateColumns: '1fr 1fr',
          padding: '0'
        }}
      >
        <button
          type="button"
          onClick={() => {
            addToCart({
              ...product,
              price: livePrice,
              selectedSize
            }, qty);
            addToast(`Added ${qty} × ${product.name} to Cart`, 'success');
            navigate('/cart');
          }}
          style={{
            backgroundColor: '#ffffff',
            color: '#212121',
            border: 'none',
            borderTop: '1px solid #e0e0e0',
            fontSize: '0.88rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            cursor: 'pointer',
            textTransform: 'uppercase'
          }}
        >
          <ShoppingBag size={18} color="#212121" /> ADD TO CART
        </button>

        <button
          type="button"
          onClick={() => {
            addToCart({
              ...product,
              price: livePrice,
              selectedSize
            }, qty);
            navigate('/checkout');
          }}
          style={{
            backgroundColor: '#fb641b',
            color: '#ffffff',
            border: 'none',
            fontSize: '0.88rem',
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            cursor: 'pointer',
            textTransform: 'uppercase',
            boxShadow: '0 -1px 4px rgba(251, 100, 27, 0.3)'
          }}
        >
          <Zap size={18} /> BUY NOW
        </button>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .quickview-grid {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            padding: 14px !important;
          }
          .quickview-grid > div:first-child {
            position: static !important;
          }
        }
        @media (max-width: 768px) {
          .fk-mobile-sticky-cta {
            display: grid !important;
          }
          .fk-pdp-page-wrap {
            padding-bottom: 64px !important;
          }
        }
        @media (max-width: 600px) {
          .quickview-grid {
            padding: 10px !important;
          }
        }
      `}</style>
    </div>
  );
}
